import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { DataTable } from "@/components/DataTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { ExcelImportDialog } from "@/components/ExcelImportDialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { Student, StudentFormData, MasterItem, ColumnFilterOption } from "@/types";

const emptyForm = (): StudentFormData => ({
  name: "",
  admission_no: "",
  class_id: "",
  section_id: "",
  email: "",
  phone: "",
  status: "active",
  issue_limit: "3",
});

export default function MembershipRegisterPage() {
  const { api, user } = useAuth();
  const [students, setStudents] = useState<Student[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Student | null>(null);
  const [classes, setClasses] = useState<MasterItem[]>([]);
  const [sections, setSections] = useState<MasterItem[]>([]);
  const [form, setForm] = useState<StudentFormData>(emptyForm);
  const [importOpen, setImportOpen] = useState(false);
  const isAdmin = user?.role === "admin" || user?.role === "librarian";
  const [columnFilters, setColumnFilters] = useState<Record<string, any>>({});
  const filterTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filteredSections = sections.filter((s) => !form.class_id || String(s.class_id) === String(form.class_id));

  const fetchStudents = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { page, limit: 100 };
      const res = await api.get("/students", { params });
      setStudents(res.data.items);
      setTotalPages(res.data.pages);
    } catch {
      toast.error("Failed to load students");
    } finally {
      setLoading(false);
    }
  }, [api, page]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    const load = async () => {
      try {
        const [c, s] = await Promise.all([api.get("/masters/classes/all"), api.get("/masters/sections/all")]);
        setClasses(c.data);
        setSections(s.data);
      } catch {}
    };
    load();
  }, [api]);

  const handleColumnFiltersChange = useCallback((filters: Record<string, any>) => {
    setColumnFilters(filters);
    if (filterTimer.current) clearTimeout(filterTimer.current);
    filterTimer.current = setTimeout(() => setPage(1), 400);
  }, []);

  const uniqueOptions = useMemo(() => {
    const names = new Map<string, string>();
    const admissions = new Map<string, string>();
    const phones = new Map<string, string>();
    for (const s of students) {
      if (s.name) names.set(s.name, s.name);
      if (s.admission_no) admissions.set(String(s.admission_no), String(s.admission_no));
      if (s.phone) phones.set(s.phone, s.phone);
    }
    const toOpts = (map: Map<string, string>) =>
      Array.from(map.entries())
        .map(([value, label]) => ({ value, label }))
        .sort((a, b) => a.label.localeCompare(b.label));
    return {
      name: toOpts(names),
      admission_no: toOpts(admissions),
      phone: toOpts(phones),
    };
  }, [students]);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setDialogOpen(true);
  };

  const openEdit = (s: Student) => {
    setEditing(s);
    setForm({
      name: s.name,
      admission_no: s.admission_no,
      class_id: s.class_id ? String(s.class_id) : "",
      section_id: s.section_id ? String(s.section_id) : "",
      email: s.email || "",
      phone: s.phone || "",
      status: s.status,
      issue_limit: String(s.issue_limit || 3),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.admission_no) {
      toast.error("Name and Admission No are required");
      return;
    }
    const payload = {
      ...form,
      class_id: form.class_id ? parseInt(String(form.class_id), 10) : null,
      section_id: form.section_id ? parseInt(String(form.section_id), 10) : null,
      issue_limit: parseInt(form.issue_limit, 10) || 3,
    };
    try {
      if (editing) {
        await api.put(`/students/${editing.id}`, payload);
        toast.success("Student updated");
      } else {
        await api.post("/students", payload);
        toast.success("Student created");
      }
      setDialogOpen(false);
      fetchStudents();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
  };

  const handleDelete = async (student: Student) => {
    const label = [student.name, student.admission_no].filter(Boolean).join(" · ");
    if (!window.confirm(`Delete student "${label}"?\n\nThis cannot be undone.`)) return;
    try {
      await api.delete(`/students/${student.id}`);
      toast.success("Deleted");
      setDialogOpen(false);
      setEditing(null);
      fetchStudents();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    }
  };

  const handleClassChange = (classId: string) => {
    setForm({ ...form, class_id: classId, section_id: "" });
  };

  const handleExport = async () => {
    try {
      const res = await api.get("/excel/export/students", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "students.xlsx";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const classOptions: ColumnFilterOption[] = classes.map((c) => ({ label: c.name, value: String(c.id) }));
  const statusOptions: ColumnFilterOption[] = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
  ];

  const hideOnMobile = "hidden md:table-cell";

  const resolveClassSection = (student: Student) => {
    const className = student.class_name || classes.find((c) => String(c.id) === String(student.class_id))?.name || "";
    const sectionName = student.section_name || sections.find((s) => String(s.id) === String(student.section_id))?.name || "";
    return [className, sectionName].filter(Boolean).join(" · ") || "—";
  };

  const columns: any[] = [
    {
      accessorKey: "name",
      header: "Name",
      meta: { filterVariant: "multi-select", filterOptions: uniqueOptions.name, filterAccessor: "name" },
      cell: ({ row }: any) => (
        <div className="min-w-0">
          <span className="font-medium leading-snug">{row.original.name}</span>
          <p className="text-sm text-muted-foreground mt-0.5 md:hidden">{resolveClassSection(row.original)}</p>
        </div>
      ),
    },
    {
      accessorKey: "admission_no",
      header: "Adm No",
      meta: { filterVariant: "multi-select", filterOptions: uniqueOptions.admission_no, filterAccessor: "admission_no" },
      cell: ({ row }: any) => <span className="font-mono text-base">{row.original.admission_no}</span>,
    },
    {
      id: "class_section",
      accessorKey: "class_id",
      header: "Class",
      meta: {
        filterVariant: "multi-select",
        filterOptions: classOptions,
        filterAccessor: "class_id",
        columnClassName: hideOnMobile,
      },
      cell: ({ row }: any) => <span>{resolveClassSection(row.original)}</span>,
    },
    {
      accessorKey: "email",
      header: "Email",
      meta: { columnClassName: hideOnMobile },
    },
    {
      accessorKey: "phone",
      header: "Phone",
      meta: { filterVariant: "multi-select", filterOptions: uniqueOptions.phone, filterAccessor: "phone", columnClassName: hideOnMobile },
    },
    {
      accessorKey: "status",
      header: "Status",
      meta: {
        filterVariant: "multi-select",
        filterOptions: statusOptions,
        filterAccessor: "status",
        columnClassName: hideOnMobile,
      },
      cell: ({ row }: any) => (
        <Badge
          variant={row.original.status === "active" ? "default" : "secondary"}
          className={row.original.status === "active" ? "bg-green-100 text-green-800 hover:bg-green-100" : ""}
        >
          {row.original.status}
        </Badge>
      ),
    },
    {
      accessorKey: "issued_count",
      header: "Issued",
      meta: { columnClassName: hideOnMobile },
      cell: ({ row }: any) => `${row.original.issued_count || 0}/${row.original.issue_limit || 3}`,
    },
    ...(isAdmin
      ? [
          {
            id: "actions",
            header: "Actions",
            meta: { columnClassName: hideOnMobile },
            cell: ({ row }: any) => (
              <div className="flex items-center gap-1" onClick={(e: any) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(row.original)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(row.original)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ),
          },
        ]
      : []),
  ];

  return (
    <div className="space-y-6" data-testid="membership-register-page">
      <div className="space-y-3 w-full min-w-0">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight whitespace-nowrap">
          Student Register
        </h1>
        {isAdmin && (
          <TooltipProvider delayDuration={200}>
            <div className="flex items-center gap-2 w-full">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={handleExport} data-testid="export-students-btn">
                    <Download className="w-4 h-4" />
                    <span className="sr-only">Export Excel</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Export Excel</TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-10 w-10 shrink-0"
                    onClick={() => setImportOpen(true)}
                    data-testid="import-students-btn"
                  >
                    <Upload className="w-4 h-4" />
                    <span className="sr-only">Import Excel</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Import Excel</TooltipContent>
              </Tooltip>
              <Button className="flex-1 min-w-0 h-10 text-sm bg-primary hover:bg-primary/90" onClick={openNew} data-testid="add-student-btn">
                <Plus className="w-4 h-4 mr-1.5 shrink-0" />
                Add Student
              </Button>
            </div>
          </TooltipProvider>
        )}
      </div>

      <DataTable
        columns={columns}
        data={students}
        totalPages={totalPages}
        currentPage={page}
        onPageChange={setPage}
        columnFilters={columnFilters}
        onColumnFiltersChange={handleColumnFiltersChange}
        loading={loading}
        headerFilters
        onRowClick={isAdmin ? openEdit : undefined}
        data-testid="students-table"
      />

      <ExcelImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        entityType="students"
        entityLabel="Students"
        templateDescription="Download the student import template with the expected columns. You can provide class and section names instead of numeric IDs."
        uploadDescription="Upload a student Excel file with name and admission number columns. Class and section names will be matched automatically, and missing classes or sections will be created."
        onSuccess={fetchStudents}
      />

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90dvh] flex flex-col gap-0 overflow-hidden p-0">
          <DialogHeader className="px-6 pt-6 pb-4 shrink-0">
            <DialogTitle>{editing ? "Edit Student" : "Add Student"}</DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 pb-4 grid grid-cols-2 gap-4">
            <div className="col-span-2 space-y-1.5">
              <Label>Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="student-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label>Admission No *</Label>
              <Input value={form.admission_no} onChange={(e) => setForm({ ...form, admission_no: e.target.value })} className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label>Class</Label>
              <Select value={form.class_id || undefined} onValueChange={handleClassChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select class" />
                </SelectTrigger>
                <SelectContent>
                  {classes.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Section</Label>
              <Select
                value={form.section_id || undefined}
                onValueChange={(v) => setForm({ ...form, section_id: v })}
                disabled={!form.class_id}
              >
                <SelectTrigger>
                  <SelectValue placeholder={form.class_id ? "Select section" : "Select class first"} />
                </SelectTrigger>
                <SelectContent>
                  {filteredSections.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </SelectItem>
                  ))}
                  {filteredSections.length === 0 && <div className="px-3 py-2 text-xs text-muted-foreground">No sections for this class</div>}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Issue Limit</Label>
              <Input type="number" value={form.issue_limit} onChange={(e) => setForm({ ...form, issue_limit: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter className="px-6 py-4 border-t bg-background shrink-0 !flex-row !flex-nowrap justify-end gap-2 space-x-0">
            {editing && (
              <Button
                className="flex-1 sm:flex-none h-11 text-base font-semibold bg-red-600 text-white hover:bg-red-700"
                onClick={() => editing && handleDelete(editing)}
                data-testid="delete-student-btn"
              >
                <Trash2 className="w-5 h-5 mr-1.5" /> Delete
              </Button>
            )}
            <Button
              className="flex-1 sm:flex-none h-11 text-base font-semibold bg-slate-200 text-slate-800 hover:bg-slate-300"
              onClick={() => setDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="flex-1 sm:flex-none h-11 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={handleSave}
              data-testid="save-student-btn"
            >
              {editing ? "Update" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
